import { useEffect, useMemo } from "react";
import { SelectField } from "../../components";
import { useFormContext } from "react-hook-form";

import { RootState } from "../../store/root-reducer";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { MiniPlanCard } from "../../components/PlanCard";

function ItemDetailes({ claimOptions, warranty_items, claim_items, plan }) {
  const { register, getValues } = useFormContext();
  const { t } = useTranslation();
  const selected_warranty_items = getValues("selectedItems");

  const { materialOptions, colorOptions, manufacturersOptions } =
    useMemo(() => {
      const {
        colors = [],
        materialTypes = [],
        manufacturers = [],
      } = claimOptions;
      const materialOptions = materialTypes.map((v) => ({
        label: v.name,
        value: v.id,
      }));
      const colorOptions = colors.map((v) => ({
        label: v.name,
        value: v.id,
      }));
      const manufacturersOptions = manufacturers.map((v) => ({
        label: v.name,
        value: v.id,
      }));
      return {
        materialOptions,
        colorOptions,
        manufacturersOptions,
      };
    }, [claimOptions]);

  const filtered_selected_warranty_items = useMemo(() => {
    const claimsByWarranty = claim_items.reduce((acc, ci) => {
      if (!acc[ci.warrntyItemId]) acc[ci.warrntyItemId] = [];
      acc[ci.warrntyItemId].push(ci);
      return acc;
    }, {});

    const usedCountByWarranty = {};

    return (
      warranty_items
        .map((warrItem) => {
          const claimsForWarranty = claimsByWarranty[warrItem.id] || [];
          const usedSoFar = usedCountByWarranty[warrItem.id] || 0;

          let claimItem = null;

          if (usedSoFar < claimsForWarranty.length) {
            claimItem = claimsForWarranty[usedSoFar];
            usedCountByWarranty[warrItem.id] = usedSoFar + 1;
          }

          if (
            claimItem ||
            selected_warranty_items.includes(warrItem?.trackKey)
          ) {
            return {
              trackKey: warrItem.trackKey,
              type: warrItem.type,
              manufacturer:
                claimItem?.manufacturer?.id || warrItem?.manufacturer?.id || "",
              material: claimItem?.material?.id || warrItem?.material?.id || "",
              color: claimItem?.color?.id || warrItem?.color?.id || "",
              claimItemId: claimItem?.id ?? null,
            };
          }

          return null;
        })
        .filter(Boolean) || []
    );
  }, [claim_items, selected_warranty_items, warranty_items]);

  if (filtered_selected_warranty_items.length === 0) {
    return (
      <div className="justify-center flex items-center flex-col py-8 text-center text-3xl font-semibold text-gray-700 ">
        <input
          type="hidden"
          {...register("temp", { required: true, shouldUnregister: true })}
        />
        <div className="w-96">
          {t("Please select some items on 2")}
          <sup>nd</sup> step
        </div>
      </div>
    );
  }

  return (
    <div>
      <MiniPlanCard plan={plan} />
      <div className="space-y-5 mt-5">
        {filtered_selected_warranty_items.map((item, idx) => (
          <ClaimItemBox
            idx={idx}
            item={item}
            key={item.trackKey}
            colorOptions={colorOptions}
            materialOptions={materialOptions}
            manufacturersOptions={manufacturersOptions}
          />
        ))}
      </div>
    </div>
  );
}

const ClaimItemBox = ({
  idx,
  item,
  colorOptions,
  materialOptions,
  manufacturersOptions,
}) => {
  const { control, setValue } = useFormContext();

  useEffect(() => {
    setValue(`claimItems.[${item.trackKey}].claimItemId`, item.claimItemId);
    return () => {};
  }, [item.claimItemId, item.trackKey, setValue]);

  return (
    <div className="space-y-2" key={item.id}>
      <div className="bg-[#EBEEEF] min-h-8 rounded flex items-center px-2 gap-2 overflow-hidden">
        <span className="size-5 flex justify-center items-center  bg-white rounded-full p-3">
          {idx + 1}
        </span>
        <div className="text-black font-semibold  block">{item.type.name}</div>
      </div>
      <SelectField
        label="Manufacturer"
        control={control}
        required
        placeholder="Select Manufacturer"
        defaultValue={item.manufacturer}
        name={`claimItems.[${item.trackKey}].manufacturer`}
        options={manufacturersOptions}
      />
      <SelectField
        required
        label="Material"
        defaultValue={item.material}
        placeholder="Select Material"
        control={control}
        name={`claimItems.[${item.trackKey}].material`}
        options={materialOptions}
      />
      <SelectField
        required
        label="Color"
        defaultValue={item.color}
        control={control}
        placeholder="Select Color"
        name={`claimItems.[${item.trackKey}].color`}
        options={colorOptions}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  claimOptions: state?.user?.claimOptions || {},
  warranty_items: state?.claim?.warranty_items || [],
  claim_items: state?.claim?.claim_items || [],
  plan: state.claim.plan || {},
});

export default connect(mapStateToProps)(ItemDetailes);
